import { Injectable } from "@angular/core";
import * as THREE from "three";
import { Object3DUserData } from "~/src/utils/shape";
import { CellReference } from "@models/mesh.model";
import { SHAPE_WEIGHTS, ShapeType } from "../../../../../utils/shape";

interface StabilityCheckResult {
    valid: boolean;
    reason?: string;
    unstableStructures?: THREE.Mesh[][];
}

@Injectable({
    providedIn: "root",
})
export class StabilityService {
    private readonly STABILITY_TOLERANCE = 5; // adjust 3–8 is good

    private readonly directions = [
        { column: 1, row: 0, cell: 0 },
        { column: -1, row: 0, cell: 0 },
        { column: 0, row: 1, cell: 0 },
        { column: 0, row: -1, cell: 0 },
        { column: 0, row: 0, cell: 1 },
        { column: 0, row: 0, cell: -1 },
    ];

    constructor() {}

    private getCenterOfMass(meshes: THREE.Mesh[]): THREE.Vector3 {
        const center = new THREE.Vector3();
        let totalWeight = 0;

        for (const mesh of meshes) {
            const weight = 20;

            center.x += mesh.position.x * weight;
            center.y += mesh.position.y * weight;
            center.z += mesh.position.z * weight;

            totalWeight += weight;
        }

        return center.divideScalar(totalWeight);
    }

    public getGroundSupports(meshes: THREE.Mesh[]): THREE.Mesh[] {
        return meshes.filter((mesh) => {
            const ref = this.getCellReference(mesh);

            return ref.cell === 0;
        });
    }

    private getSupportBounds(groundMeshes: THREE.Mesh[]) {
        let minX = Infinity;
        let maxX = -Infinity;

        let minZ = Infinity;
        let maxZ = -Infinity;
        let HALF_GRID_SIZE = 12.5;

        for (const mesh of groundMeshes) {
            minX = Math.min(minX, mesh.position.x - HALF_GRID_SIZE);
            maxX = Math.max(maxX, mesh.position.x + HALF_GRID_SIZE);

            minZ = Math.min(minZ, mesh.position.z - HALF_GRID_SIZE);
            maxZ = Math.max(maxZ, mesh.position.z + HALF_GRID_SIZE);
        }

        return { minX, maxX, minZ, maxZ };
    }

    public isStable(meshes: THREE.Mesh[]): boolean {
        const supports = this.getGroundSupports(meshes);

        if (supports.length === 0) {
            return false;
        }

        const com = this.getCenterOfMass(meshes);
        const bounds = this.getSupportBounds(supports);

        const stable =
            com.x >= bounds.minX - this.STABILITY_TOLERANCE &&
            com.x <= bounds.maxX + this.STABILITY_TOLERANCE &&
            com.z >= bounds.minZ - this.STABILITY_TOLERANCE &&
            com.z <= bounds.maxZ + this.STABILITY_TOLERANCE;

        return stable;
    }

    public checkStability(meshes: THREE.Mesh[]): StabilityCheckResult {
        const structures = this.getConnectedStructures(meshes);

        const unstableStructures = structures.filter((structure) => !this.isStable(structure));

        if (unstableStructures.length > 0) {
            return {
                valid: false,
                reason: `${unstableStructures.length} unstable structure(s) found.`,
                unstableStructures,
            };
        }

        return {
            valid: true,
            reason: "All structures are stable.",
            unstableStructures: [],
        };
    }

    private getCellReference(mesh: THREE.Mesh): CellReference {
        return mesh.userData[Object3DUserData.cellReference];
    }

    private getKey(ref: CellReference): string {
        return `${ref.column},${ref.row},${ref.cell}`;
    }

    private getConnectedStructures(meshes: THREE.Mesh[]): THREE.Mesh[][] {
        const meshMap = new Map<string, THREE.Mesh>();

        for (const mesh of meshes) {
            const ref = this.getCellReference(mesh);
            meshMap.set(this.getKey(ref), mesh);
        }

        const visited = new Set<string>();
        const structures: THREE.Mesh[][] = [];

        for (const startMesh of meshes) {
            const startRef = this.getCellReference(startMesh);
            const startKey = this.getKey(startRef);

            if (visited.has(startKey)) {
                continue;
            }

            const structure: THREE.Mesh[] = [];
            const queue: THREE.Mesh[] = [startMesh];

            visited.add(startKey);

            let head = 0;

            while (head < queue.length) {
                const current = queue[head++];
                structure.push(current);

                const currentRef = this.getCellReference(current);

                for (const dir of this.directions) {
                    const neighborRef: CellReference = {
                        column: currentRef.column + dir.column,
                        row: currentRef.row + dir.row,
                        cell: currentRef.cell + dir.cell,
                    };

                    const neighborKey = this.getKey(neighborRef);

                    if (visited.has(neighborKey)) {
                        continue;
                    }

                    const neighbor = meshMap.get(neighborKey);

                    if (neighbor) {
                        visited.add(neighborKey);
                        queue.push(neighbor);
                    }
                }
            }

            structures.push(structure);
        }

        return structures;
    }

    private getWeight(mesh: THREE.Mesh): number {
        return SHAPE_WEIGHTS[mesh.name as ShapeType];
    }
}
